import React, { useEffect, useState } from 'react';
import {
    Card,
    Typography,
    Button,
    Form,
    Input,
    InputNumber,
    DatePicker,
    ConfigProvider,
    Checkbox,
    Radio,
    Tooltip,
    Space,
} from 'antd';
import {
    CalendarOutlined,
    SaveOutlined,
    ReloadOutlined,
    PlusOutlined,
    MinusCircleOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import locale from 'antd/locale/lt_LT';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getPlanningAdministration, createOrUpdatePlanningAdministration } from '../../core/center-accreditation/centerAccreditationApi';

import './ScPlanningAdministration.scss';

// Set Lithuanian locale for dayjs
dayjs.locale('lt');

const { Text } = Typography;
const { TextArea } = Input;

// 9.1 Plan types
const planTypes = [
    { code: 'strateginis', title: '9.1.2 – strateginis' },
    { code: 'metinis', title: '9.1.3 – metinis' },
    { code: 'renginių', title: 'renginių' },
    { code: 'kiti', title: '9.1.4 – kiti planai' },
];

// 9.2 Goals formulation basis
const goalsFormulationBasisOptions = [
    { code: 'strateginiais_dokumentais', title: '9.2.1 – suformuluoti remiantis strateginiais švietimo sistemos dokumentais' },
    { code: 'steigėjo_tikslais', title: '9.2.2 – institucijos tikslai, uždaviniai ir funkcijos suformuluoti remiantis steigėjo tikslais ir uždaviniais' },
    { code: 'savus_tikslus', title: '9.2.3 – institucija formulavo savus tikslus, uždavinius, funkcijas' },
];

// 9.4 Report purposes
const reportPurposesOptions = [
    { code: 'atsiskaityti_klientams', title: '9.4.1 – atsiskaityti klientams' },
    { code: 'atsiskaityti_steigėjui', title: '9.4.2 – atsiskaityti steigėjui' },
    { code: 'savianalizei', title: '9.4.3 – savianalizei ir problemų identifikavimui' },
    { code: 'reklamai', title: '9.4.4 – institucijos reklamai' },
    { code: 'viešam_pristatymui', title: '9.4.5 – viešam veiklos pristatymui' },
    { code: 'nerengiame', title: '9.4.6 – ataskaitų nerengiame' },
];

// 9.5 Report frequency
const reportFrequencyOptions = [
    { code: 'kalendoriniai_metai', title: '9.5.1 – už kalendorinius metus' },
    { code: 'mokslo_metai', title: '9.5.2 – už mokslo metus' },
    { code: 'kita', title: '9.5.3 – kita (įrašykite)' },
];

// 9.6 Specific themes priorities
const specificThemesPriorities = [
    'Kompetencijų tobulinimas įgyvendinant šiuolaikinį ugdymo/mokymo turinį.',
    'Kompetencijų, reikalingų veiksmingai ugdyti skirtingų ugdymosi poreikių turinčius mokinius, tobulinimas.',
    'Vadovavimo ir lyderystės ugdymo/mokymo procesui ir švietimo įstaigai kompetencijų tobulinimas.',
    'Vaikų psichinės ir fizinės sveikatos stiprinimas.',
    'STEAM dalykų ugdymo kompetencijų tobulinimas.',
    'Pedagogų užsienio kalbų, informacinių technologijų ir bendrųjų kompetencijų tobulinimas.',
];

const ScPlanningAdministration: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { planningAdministration, isPlanningAdministrationFetching } = useSelector((state: CombinedStates) => state.centerAccreditation);
    const { singleSchool } = useSelector((state: CombinedStates) => state.school);

    const [form] = Form.useForm();
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());
    const [reportFrequencyOther, setReportFrequencyOther] = useState<string>('');

    useEffect(() => {
        dispatch(getPlanningAdministration({ date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null }));
    }, [dispatch, selectedDate]);

    useEffect(() => {
        if (planningAdministration) {
            // Initialize plans
            const plans = planningAdministration.plans || planTypes.map(type => ({
                code: type.code,
                title: type.title,
                from: null,
                to: null,
                exists: false,
                description: '',
            }));

            form.setFieldsValue({
                plans,
                goals_formulation_basis: planningAdministration.goals_formulation_basis || [],
                employees_familiar_with_job_descriptions: planningAdministration.employees_familiar_with_job_descriptions || false,
                employees_familiar_with_work_rules: planningAdministration.employees_familiar_with_work_rules || false,
                report_purposes: planningAdministration.report_purposes || [],
                report_frequency: planningAdministration.report_frequency || null,
                specific_themes_emphasized: planningAdministration.specific_themes_emphasized || false,
                specific_themes_list: planningAdministration.specific_themes_list || [],
            });

            // Set "other" text fields
            setReportFrequencyOther(planningAdministration.report_frequency_other || '');
        } else {
            // Initialize with default values
            form.setFieldsValue({
                plans: planTypes.map(type => ({
                    code: type.code,
                    title: type.title,
                    from: null,
                    to: null,
                    exists: false,
                    description: '',
                })),
                goals_formulation_basis: [],
                employees_familiar_with_job_descriptions: false,
                employees_familiar_with_work_rules: false,
                report_purposes: [],
                report_frequency: null,
                specific_themes_emphasized: false,
                specific_themes_list: [],
            });
        }
    }, [planningAdministration, form]);

    const handleSave = async (values: any) => {
        // Transform plans
        const plans = (values.plans || []).map((item: any) => ({
            code: item.code,
            title: item.title,
            from: item.exists && (item.code === 'strateginis' || item.code === 'metinis') ? (item.from || null) : null,
            to: item.exists && (item.code === 'strateginis' || item.code === 'metinis') ? (item.to || null) : null,
            exists: item.exists || false,
            description: item.exists && (item.code === 'renginių' || item.code === 'kiti') ? (item.description || '') : '',
        }));

        const requestData: any = {
            ...(planningAdministration?.id && { id: planningAdministration.id }),
            date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null,
            plans: plans.length > 0 ? plans : null,
            goals_formulation_basis: values.goals_formulation_basis && values.goals_formulation_basis.length > 0 ? values.goals_formulation_basis : null,
            employees_familiar_with_job_descriptions: values.employees_familiar_with_job_descriptions || false,
            employees_familiar_with_work_rules: values.employees_familiar_with_work_rules || false,
            report_purposes: values.report_purposes && values.report_purposes.length > 0 ? values.report_purposes : null,
            report_frequency: values.report_frequency || null,
            report_frequency_other: reportFrequencyOther || null,
            specific_themes_emphasized: values.specific_themes_emphasized || false,
            specific_themes_list: values.specific_themes_emphasized && values.specific_themes_list && values.specific_themes_list.length > 0 ? values.specific_themes_list : null,
        };

        await dispatch(createOrUpdatePlanningAdministration(requestData));
    };

    const handleRefresh = (date?: dayjs.Dayjs) => {
        const dateToUse = date || selectedDate;
        dispatch(getPlanningAdministration({ date: dateToUse ? dateToUse.format('YYYY-MM-DD') : null }));
    };

    const handleRefreshClick = () => {
        handleRefresh();
    };

    return (
        <ConfigProvider locale={locale}>
            <div className="school-planning-administration">
                <Card title="Planavimas ir administravimas DIREKTORĖ" className="planning-administration-form-card" loading={isPlanningAdministrationFetching || false}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            {planningAdministration?.period_description ? (
                                <Text strong style={{ fontSize: '16px' }}>
                                    <CalendarOutlined style={{ marginRight: 8 }} />
                                    {planningAdministration.period_description}
                                </Text>
                            ) : (
                                <Text strong style={{ fontSize: '16px' }}>
                                    Pasirinktam laikotarpiui duomenys nepateikti, norėdami pateikti duomenis užpildykite ir išsaugokite formą.
                                </Text>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <DatePicker
                                placeholder="Pasirinkite datą"
                                value={selectedDate}
                                onChange={(date) => {
                                    const newDate = date || dayjs();
                                    setSelectedDate(newDate);
                                    handleRefresh(newDate);
                                }}
                                format="YYYY-MM-DD"
                                style={{ width: 180 }}
                                allowClear={false}
                                disabledDate={(current) => current && current > dayjs().endOf('day')} />
                            <Tooltip title="Atnaujinti planavimo ir administravimo duomenis">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefreshClick}
                                    loading={!!isPlanningAdministrationFetching}>
                                    Atnaujinti
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                        initialValues={{
                            plans: planTypes.map(type => ({
                                code: type.code,
                                title: type.title,
                                from: null,
                                to: null,
                                exists: false,
                                description: '',
                            })),
                            goals_formulation_basis: [],
                            employees_familiar_with_job_descriptions: false,
                            employees_familiar_with_work_rules: false,
                            report_purposes: [],
                            report_frequency: null,
                            specific_themes_emphasized: false,
                            specific_themes_list: [],
                        }}>

                        {/* 9.1 Plans */}
                        <Card title="9.1 Kokius planus ir kokiam laikotarpiui rengiate? (įrašykite)" style={{ marginBottom: 16 }}>
                            <Form.List name="plans">
                                {(fields) => (
                                    <>
                                        {fields.map((field, index) => {
                                            const planType = planTypes[index];
                                            const isDateRange = planType?.code === 'strateginis' || planType?.code === 'metinis';
                                            const isTextInput = planType?.code === 'renginių' || planType?.code === 'kiti';

                                            return (
                                                <div key={field.key} style={{ marginBottom: 16 }}>
                                                    <Form.Item name={[field.name, 'exists']} valuePropName="checked" style={{ marginBottom: 8 }}>
                                                        <Checkbox>{planType?.title}</Checkbox>
                                                    </Form.Item>
                                                    <Form.Item
                                                        noStyle
                                                        shouldUpdate={(prevValues, currentValues) => {
                                                            const prevPlan = prevValues.plans?.[index];
                                                            const currentPlan = currentValues.plans?.[index];
                                                            return prevPlan?.exists !== currentPlan?.exists;
                                                        }}>
                                                        {({ getFieldValue }) => {
                                                            const planExists = getFieldValue(['plans', index, 'exists']);
                                                            if (!planExists) return null;

                                                            if (isDateRange) {
                                                                return (
                                                                    <Space style={{ marginLeft: 24 }}>
                                                                        <Form.Item name={[field.name, 'from']} label="Nuo" style={{ margin: 0 }}>
                                                                            <InputNumber min={1900} max={2100} placeholder="Metai" style={{ width: 120 }} />
                                                                        </Form.Item>
                                                                        <Form.Item name={[field.name, 'to']} label="Iki" style={{ margin: 0 }}>
                                                                            <InputNumber min={1900} max={2100} placeholder="Metai" style={{ width: 120 }} />
                                                                        </Form.Item>
                                                                    </Space>
                                                                );
                                                            }

                                                            if (isTextInput) {
                                                                return (
                                                                    <Form.Item name={[field.name, 'description']} style={{ marginLeft: 24, marginBottom: 0 }}>
                                                                        <TextArea rows={2} placeholder="Įrašykite" />
                                                                    </Form.Item>
                                                                );
                                                            }

                                                            return null;
                                                        }}
                                                    </Form.Item>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </Form.List>
                        </Card>

                        {/* 9.2 Goals formulation basis */}
                        <Card title="9.2 Kokie yra institucijos nuostatuose suformuluoti tikslai, uždaviniai, funkcijos?" style={{ marginBottom: 16 }}>
                            <Form.Item name="goals_formulation_basis">
                                <Checkbox.Group>
                                    {goalsFormulationBasisOptions.map((option) => (
                                        <div key={option.code} style={{ marginBottom: 8 }}>
                                            <Checkbox value={option.code}>{option.title}</Checkbox>
                                        </div>
                                    ))}
                                </Checkbox.Group>
                            </Form.Item>
                        </Card>

                        {/* 9.3 Employees familiar with documents */}
                        <Card title="9.3 Ar darbuotojai supažindinti ir savo veikloje vadovaujasi šiais dokumentais?" style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>Institucijos vadovo ir darbuotojų pareiginiais nuostatais</Text>
                                <Form.Item name="employees_familiar_with_job_descriptions" style={{ marginTop: 8 }}>
                                    <Radio.Group>
                                        <Radio value>9.3.1 – taip</Radio>
                                        <Radio value={false}>9.3.2 – ne</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </div>
                            <div>
                                <Text strong>Institucijos darbo tvarkos taisyklėmis</Text>
                                <Form.Item name="employees_familiar_with_work_rules" style={{ marginTop: 8 }}>
                                    <Radio.Group>
                                        <Radio value>9.3.3 – taip</Radio>
                                        <Radio value={false}>9.3.4 – ne</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </div>
                        </Card>

                        {/* 9.4 Report purposes */}
                        <Card title="9.4 Kokiu tikslu rengiamos veiklos ataskaitos?" style={{ marginBottom: 16 }}>
                            <Form.Item name="report_purposes">
                                <Checkbox.Group>
                                    {reportPurposesOptions.map((option) => (
                                        <div key={option.code} style={{ marginBottom: 8 }}>
                                            <Checkbox value={option.code}>{option.title}</Checkbox>
                                        </div>
                                    ))}
                                </Checkbox.Group>
                            </Form.Item>
                        </Card>

                        {/* 9.5 Report frequency */}
                        <Card title="9.5 Kaip dažnai rengiamos veiklos ataskaitos?" style={{ marginBottom: 16 }}>
                            <Form.Item name="report_frequency">
                                <Radio.Group>
                                    {reportFrequencyOptions.map((option) => (
                                        <Radio key={option.code} value={option.code}>
                                            {option.title}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.report_frequency !== currentValues.report_frequency}>
                                {({ getFieldValue }) => {
                                    const frequency = getFieldValue('report_frequency');
                                    return frequency === 'kita' ? (
                                        <Form.Item style={{ marginTop: 8 }}>
                                            <Input
                                                placeholder="Įrašykite"
                                                value={reportFrequencyOther}
                                                onChange={(e) => setReportFrequencyOther(e.target.value)} />
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>
                        </Card>

                        {/* 9.6 Specific themes emphasized */}
                        <Card title="9.6 Ar savo veikloje institucija akcentuoja kokią nors specifinę tematiką ar veiklos sritį?" style={{ marginBottom: 16 }}>
                            <Form.Item name="specific_themes_emphasized">
                                <Radio.Group>
                                    <Radio value>9.6.1 – taip, savo veikloje akcentuojame (įrašykite) šiuos prioritetus:</Radio>
                                    <Radio value={false}>9.6.2 – ne, visoms sritims skiriamas vienodas dėmesys</Radio>
                                </Radio.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.specific_themes_emphasized !== currentValues.specific_themes_emphasized}>
                                {({ getFieldValue }) => {
                                    const emphasized = getFieldValue('specific_themes_emphasized');
                                    return emphasized ? (
                                        <Form.List name="specific_themes_list">
                                            {(fields, { add, remove }) => (
                                                <>
                                                    {fields.map((field) => (
                                                        <Form.Item key={field.key} style={{ marginBottom: 8 }}>
                                                            <Space style={{ display: 'flex', marginBottom: 0 }} align="baseline">
                                                                <Form.Item
                                                                    name={[field.name]}
                                                                    style={{ margin: 0, width: '100%' }}>
                                                                    <TextArea rows={2} placeholder="Įrašykite prioritetą" />
                                                                </Form.Item>
                                                                {fields.length > 0 && (
                                                                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                                                                )}
                                                            </Space>
                                                        </Form.Item>
                                                    ))}
                                                    <Form.Item>
                                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                                            Pridėti prioritetą
                                                        </Button>
                                                    </Form.Item>
                                                </>
                                            )}
                                        </Form.List>
                                    ) : null;
                                }}
                            </Form.Item>
                        </Card>

                        {/* Form Actions */}
                        <div style={{ textAlign: 'right', marginTop: 24 }}>
                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                Išsaugoti
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>
        </ConfigProvider>
    );
};

export default ScPlanningAdministration;
